// src/UserContext.tsx
import React, { createContext, useState, useContext, ReactNode } from 'react';
import { User } from './compos/SinglePostHelper/SinglePostHelper';
import { BACKEND_URL } from './config';
interface UserContextProps {
	user: User | null;
	set_user: React.Dispatch<React.SetStateAction<User | null>>;
	isAdmin: boolean;
	setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
	isLoggedIn: boolean;
	setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
	getPostId: string;
	setGetPostId: React.Dispatch<React.SetStateAction<string>>;
	isAuthLoading: boolean;
	setIsAuthLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const [user, set_user] = useState<User | null>(null);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
	const [getPostId, setGetPostId] = useState<string>('');
	// Auth now rides on an httpOnly cookie (B4.5) which JS can't read, so we track
	// a non-sensitive `isAuthed` flag for the UI and confirm the session by
	// calling myProfile (the cookie is sent via credentials:'include').
	const [isAuthLoading, setIsAuthLoading] = useState<boolean>(!!localStorage.getItem('isAuthed'));

	React.useEffect(() => {
		const authed = localStorage.getItem('isAuthed');
		if (authed) {
			const profileRequestOptions = {
				method: 'GET',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
			};

			fetch(`${BACKEND_URL}/user/myProfile`, profileRequestOptions)
				.then((response) => response.json())
				.then((result) => {
					if (result.id) {
						set_user(result);
						setIsLoggedIn(true);

						// Check if admin
						fetch(`${BACKEND_URL}/api/isAdmin`, {
							method: 'GET',
							credentials: 'include',
						})
							.then((res) => {
								if (!res.ok) throw new Error('Not an admin');
								return res.json();
							})
							.then((data) => {
								setIsAdmin(!!data);
								setIsAuthLoading(false);
							})
							.catch(() => {
								setIsAdmin(false);
								setIsAuthLoading(false);
							});
					} else {
						setIsAuthLoading(false);
					}
				})
				.catch((error) => {
					console.log('Error occurred while fetching profile:', error);
					setIsAuthLoading(false);
				});
		} else {
			setIsAuthLoading(false);
		}
	}, []);

	// Listen for auth changes made in OTHER tabs (e.g. logout / login).
	// The 'storage' event fires in every tab EXCEPT the one that made the change.
	React.useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key !== 'isAuthed') return;

			if (!e.newValue) {
				// Flag was removed → logged out in another tab
				set_user(null);
				setIsLoggedIn(false);
				setIsAdmin(false);
				setIsAuthLoading(false);
			} else {
				// Flag was set → logged in (possibly as a different user) in another tab.
				// Re-run the full auth check (cookie is sent via credentials) so this
				// tab reflects the new session.
				setIsAuthLoading(true);
				fetch(`${BACKEND_URL}/user/myProfile`, {
					method: 'GET',
					redirect: 'follow',
					credentials: 'include',
				})
					.then((res) => res.json())
					.then((result) => {
						if (result.id) {
							set_user(result);
							setIsLoggedIn(true);
							return fetch(`${BACKEND_URL}/api/isAdmin`, {
								method: 'GET',
								credentials: 'include',
							});
						}
						throw new Error('No user id');
					})
					.then((res) => {
						if (!res.ok) throw new Error('Not admin');
						return res.json();
					})
					.then((data) => {
						setIsAdmin(!!data);
						setIsAuthLoading(false);
					})
					.catch(() => {
						setIsAdmin(false);
						setIsAuthLoading(false);
					});
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	return (
		<UserContext.Provider
			value={{
				user,
				set_user,
				isAdmin,
				setIsAdmin,
				isLoggedIn,
				setIsLoggedIn,
				getPostId,
				setGetPostId,
				isAuthLoading,
				setIsAuthLoading,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

export const useUser = (): UserContextProps => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUser must be used within a UserProvider');
	}
	return context;
};
